import { Link } from 'react-router-dom';
import { Wordmark } from './Login';
import AgentChat from './home/AgentChat';
import LivePipeline from './home/LivePipeline';
import SequencerLogos from './home/SequencerLogos';

/** Public marketing homepage. Structurally modeled after outengine.com
 * (problem → features → how-it-works → pricing → FAQ → CTA), with the
 * Signaliz aesthetic — dark canvas, gradient orbs, Playfair Display for
 * the display heading, glass cards. */
export default function Home() {
  return (
    <div className="min-h-full relative overflow-hidden bg-bg text-ink">
      {/* Multi-orb gradient backdrop, Signaliz-style. */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[1100px] h-[1100px] rounded-full bg-accent/12 blur-3xl orb-drift-slow" />
        <div className="absolute top-[40%] -left-32 w-[600px] h-[600px] rounded-full bg-run/8 blur-3xl orb-drift-rev" />
        <div className="absolute top-[60%] -right-40 w-[700px] h-[700px] rounded-full bg-emerald-500/6 blur-3xl orb-drift-fast" />
        <div className="absolute -bottom-40 left-1/3 w-[500px] h-[500px] rounded-full bg-accent/8 blur-3xl orb-drift-slow" />
        <div className="absolute inset-0 grid-lines opacity-25" />
      </div>

      <div className="relative">
        {/* Top nav — sticky so it stays accessible after the visitor
            jumps to a section or scrolls back up partially. Translucent
            so the gradient backdrop bleeds through. */}
        <div className="sticky top-0 z-30 backdrop-blur-md bg-bg/70">
        <nav className="max-w-6xl mx-auto px-8 py-4 flex items-center justify-between">
          <Link to="/" className="hover:opacity-80 transition">
            <Wordmark className="h-6" />
          </Link>
          <div className="hidden md:flex items-center gap-7 text-sm text-dim">
            <a href="#features" className="hover:text-ink transition">Features</a>
            <a href="#how" className="hover:text-ink transition">How it works</a>
            <a href="#pricing" className="hover:text-ink transition">Pricing</a>
            <a href="#faq" className="hover:text-ink transition">FAQ</a>
            <a
              href="/api/docs" target="_blank" rel="noopener noreferrer"
              className="hover:text-ink transition flex items-center gap-1"
            >
              API &amp; MCP docs <span className="text-[10px] opacity-60">↗</span>
            </a>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/login" className="btn-ghost btn-sm">Sign in</Link>
            <Link to="/signup" className="btn-primary btn-sm">Get started</Link>
          </div>
        </nav>
        </div>

        {/* ── Hero ───────────────────────────────────────────────────── */}
        <section className="max-w-5xl mx-auto px-8 pt-32 pb-24 text-center animate-slide-up">
          <div className="text-[11px] uppercase tracking-[0.2em] text-accent mb-5">
            For GTM teams &amp; solo operators
          </div>
          <h1 className="font-serif text-5xl md:text-7xl tracking-tight leading-[1.02] mb-7 text-balance">
            <span className="text-ink">Stand up </span>
            <span className="text-ink italic">cold-email infra</span>
            <br />
            <span className="bg-gradient-to-r from-accent via-run to-emerald-400 bg-clip-text text-transparent italic gradient-flow">today, not weeks.</span>
          </h1>
          <p className="text-base md:text-lg text-dim leading-relaxed max-w-2xl mx-auto mb-10 text-balance">
            Built for anyone who runs cold email — GTM teams at scale or
            solo operators shipping their first 100 sends. One Microsoft 365
            tenant in, up to 150 mailboxes out — every one automatically
            connected to your Instantly or Smartlead sequencer. Click through it,{' '}
            <code className="font-mono text-[0.85em] text-ink bg-white/[0.04] px-1.5 py-0.5 rounded">curl</code>{' '}
            it, or just <span className="text-ink">tell Claude what you want.</span>
          </p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <Link to="/signup" className="btn-primary btn-lg">Get started →</Link>
            <a href="#how" className="btn btn-lg">See how it works</a>
          </div>
          <p className="text-xs text-dim mt-6">
            Bring your own tenants or buy from us. Cancel any time.
          </p>
        </section>

        {/* ── Sequencer logos strip ─────────────────────────────────── */}
        <section className="max-w-4xl mx-auto px-8 pb-24">
          <SequencerLogos />
        </section>

        {/* ── Agentic showcase ──────────────────────────────────────── */}
        <section className="max-w-5xl mx-auto px-8 pb-24">
          <div className="grid lg:grid-cols-[1.1fr_1fr] gap-8 items-center">
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-accent mb-3">Built for agents</div>
              <h2 className="font-serif text-3xl md:text-5xl tracking-tight mb-5 leading-[1.05]">
                Or just <span className="italic">ask Claude</span> to build it.
              </h2>
              <p className="text-sm md:text-base text-dim leading-relaxed mb-5">
                Every primitive in Sendepot is exposed as an MCP tool and a
                REST endpoint, secured by the same workspace-scoped tokens.
                Drop the connector into Claude Desktop, Claude Code, or
                Cursor and provision sending infrastructure in plain
                English — whether it's your CRO, your RevOps engineer, or
                you running solo. Same actions, same audit trail, same
                blast radius as the UI.
              </p>
              <ul className="text-sm text-dim space-y-2 mb-7">
                <li className="flex items-start gap-2">
                  <span className="text-accent mt-0.5">→</span>
                  <span><span className="text-ink font-medium">8 tools</span> available out of the box: list/get tenants, create, watch progress, manage sequencer keys, export credentials.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-accent mt-0.5">→</span>
                  <span><span className="text-ink font-medium">One workspace per token</span> — agents can't see, touch, or break anything outside their boundary.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-accent mt-0.5">→</span>
                  <span><span className="text-ink font-medium">Streamable HTTP</span> transport — works natively in Claude Desktop's Connectors UI, no <span className="font-mono text-[0.92em]">npx</span> wrappers.</span>
                </li>
              </ul>
              <div className="flex items-center gap-3 flex-wrap">
                <Link to="/docs/agents/overview" className="btn-primary">
                  Connect your agent →
                </Link>
                <a href="/api/docs" target="_blank" rel="noopener noreferrer" className="btn">
                  Browse the API
                </a>
              </div>
            </div>

            <AgentChat />
          </div>
        </section>

        {/* ── Live pipeline ─────────────────────────────────────────── */}
        <section className="max-w-5xl mx-auto px-8 pb-24">
          <div className="grid lg:grid-cols-[1fr_1.05fr] gap-8 items-center">
            <LivePipeline />
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-accent mb-3">What you ship same-day</div>
              <h2 className="font-serif text-3xl md:text-5xl tracking-tight mb-5 leading-[1.05]">
                Hand off the tenant. <span className="italic">We handle the rest.</span>
              </h2>
              <p className="text-sm md:text-base text-dim leading-relaxed mb-5">
                Drop in a Microsoft 365 tenant and a sending domain.
                Sendepot takes it from there. You get a real-time progress
                view as your inboxes go from zero to ready-to-send — no
                spreadsheets, no DNS edits, no waiting on a reseller.
              </p>
              <ul className="text-sm text-dim space-y-2 mb-5">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-0.5">✓</span>
                  <span><span className="text-ink">25–150 mailboxes per tenant</span>, you choose — name permutation handled automatically across one or more seed names.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-0.5">✓</span>
                  <span><span className="text-ink">Self-healing</span> — transient failures retry automatically. No paging in the middle of the night.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-0.5">✓</span>
                  <span><span className="text-ink">Same-day delivery</span> — typically a few hours from "tenant + domain" to "every mailbox connected and ready to warm up."</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* ── Problem / value prop ──────────────────────────────────── */}
        <section className="max-w-5xl mx-auto px-8 pb-24">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="text-[11px] uppercase tracking-[0.18em] text-accent mb-3">Why Sendepot exists</div>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight mb-4">
              Stop paying inbox resellers <span className="italic text-dim">a 95% markup.</span>
            </h2>
            <p className="text-sm md:text-base text-dim leading-relaxed">
              Resellers charge $1+ per mailbox for Azure infrastructure that
              costs around 5¢. Sendepot is the wholesale path — you bring
              tenants, we run the entire provisioning pipeline end-to-end
              and hand you back up to 150 mailboxes per tenant, automatically connected
              to your sequencer and ready to warm up. Same Azure inboxes,
              fraction of the cost.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            <Stat n="Up to 150" label="mailboxes per tenant" />
            <Stat n="Same day" label="from credentials to ready-to-send" />
            <Stat n="2" label="sequencers live · more shipping" />
          </div>
        </section>

        {/* ── Features ──────────────────────────────────────────────── */}
        <section id="features" className="max-w-5xl mx-auto px-8 pb-24">
          <div className="text-center mb-12">
            <div className="text-[11px] uppercase tracking-[0.18em] text-accent mb-3">What's included</div>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight">
              Every step <span className="italic">automated.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <FeatureCard
              kicker="Provisioning"
              title="25–150 mailboxes per domain, hands-free"
              body="Pick how many mailboxes you want per tenant — anywhere from 25 to 150. Sendepot generates name permutations across one or more seed names, creates the users, attaches licenses, and rotates passwords. Hands-off."
            />
            <FeatureCard
              kicker="DNS"
              title="SPF · DKIM · DMARC · Autodiscover"
              body="Point your domain's nameservers at Sendepot once — we manage the zone end to end and push MX, SPF, DKIM, DMARC, and verification records as the pipeline needs them. Domain swaps re-run the full record set on the new domain in one click."
            />
            <FeatureCard
              kicker="Sequencers"
              title="Auto-upload to Instantly or Smartlead"
              body="Every mailbox is automatically connected to your Instantly or Smartlead sequencer the moment it's provisioned — no manual upload, no copy-pasting credentials. Multiple keys per workspace — pin tenants to specific sequencer accounts via label."
            />
            <FeatureCard
              kicker="Agentic"
              title="API + MCP for AI agents"
              body='"Claude, create a tenant for example.com" works literally as a sentence. The MCP server exposes the same primitives as the REST API so agents can list tenants, watch progress, or kick off provisioning end-to-end.'
            />
            <FeatureCard
              kicker="Operations"
              title="Bulk import, bulk export"
              body="Paste a CSV to onboard ten tenants at once. Tick checkboxes on the tenant list and download a combined credentials CSV for any sequencer we don't auto-upload to yet."
            />
            <FeatureCard
              kicker="Recovery"
              title="Reconnect & domain swap"
              body="Sequencer dropped a mailbox? One-click reconnect from the dashboard. Domain burned out? Run a domain swap and every mailbox gets a new alias on the new domain in one operation."
            />
          </div>
        </section>

        {/* ── How it works ──────────────────────────────────────────── */}
        <section id="how" className="max-w-5xl mx-auto px-8 pb-24">
          <div className="text-center mb-12">
            <div className="text-[11px] uppercase tracking-[0.18em] text-accent mb-3">How it works</div>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight">
              Six steps. <span className="italic">Most of them are waiting.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <Step n="1" title="Sign up" body="Email + magic link. 60 seconds. No card." />
            <Step n="2" title="Connect your sequencer" body="Drop in your Instantly or Smartlead API key once. From then on, mailboxes upload automatically." />
            <Step n="3" title="Add a tenant" body="Bring a Microsoft 365 tenant — yours, a CSP's, or buy from us at $15/mo. Hand us the admin login + sending domain." />
            <Step n="4" title="Provisioning runs" body="Sendepot takes it from there — fully automated, fully watchable from a live progress bar. Nothing for you to click." />
            <Step n="5" title="Your mailboxes show up" body="25–150 per tenant, the count you picked. Connected to your sequencer, ready to warm up. Same Azure inboxes resellers sell — at a fraction of the cost." />
            <Step n="6" title="Send" body="Warm up per the schedules in our docs. Rotate domains every 2–4 weeks. Repeat." />
          </div>
        </section>

        {/* ── Pricing ───────────────────────────────────────────────── */}
        <section id="pricing" className="max-w-5xl mx-auto px-8 pb-24">
          <div className="text-center mb-12">
            <div className="text-[11px] uppercase tracking-[0.18em] text-accent mb-3">Pricing</div>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight mb-3">
              Pay for the <span className="italic">platform.</span> Bring your own tenants.
            </h2>
            <p className="text-sm text-dim max-w-xl mx-auto">
              All plans include the full feature set — API, MCP, domain swap, multi-sequencer keys.
              Tenant hosting is included up to your tier; overage billed per tenant.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
            <PriceCard
              tier="Starter"
              price="$97"
              cadence="/month"
              tagline="Get sending fast."
              features={[
                '5 tenants hosted',
                '$20/mo per additional tenant hosted',
                'Instantly + Smartlead live',
                'API + MCP access',
                'Email support',
              ]}
            />
            <PriceCard
              tier="Agency"
              price="$247"
              cadence="/month"
              tagline="Most popular for cold-email teams &amp; agencies."
              popular
              features={[
                '50 tenants hosted',
                '$5/mo per additional tenant hosted',
                'Multiple sequencer keys per workspace',
                'Bulk CSV import + export',
                'Priority support',
              ]}
            />
            <PriceCard
              tier="Unlimited"
              price="$597"
              cadence="/month"
              tagline="Scale without thinking about it."
              features={[
                '500 tenants hosted',
                '$1/mo per additional tenant hosted',
                'Everything in Agency',
                'Direct support channel',
                'Early access to new sequencers',
              ]}
            />
            <PriceCard
              tier="Enterprise"
              price="Custom"
              cadence=""
              tagline="For high-volume operators."
              ctaLabel="Talk to sales"
              ctaHref="mailto:support@sendepot.com?subject=Enterprise%20plan"
              features={[
                'Custom tenant volume',
                'Dedicated infra capacity',
                'White-glove onboarding',
                'SLA + named support',
                'Volume pricing on tenants from us',
              ]}
            />
          </div>
          <p className="text-center text-xs text-dim mt-6">
            All tiers month-to-month · Cancel any time · Tenants sourced separately
            ($5/mo self-managed, $12/mo from us).
          </p>
        </section>

        {/* ── FAQ ───────────────────────────────────────────────────── */}
        <section id="faq" className="max-w-3xl mx-auto px-8 pb-24">
          <div className="text-center mb-12">
            <div className="text-[11px] uppercase tracking-[0.18em] text-accent mb-3">Questions</div>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight">
              The <span className="italic">honest</span> answers.
            </h2>
          </div>
          <div className="space-y-3">
            <Faq
              q="What's a Microsoft tenant — and why do I need to bring one?"
              a="A Microsoft 365 tenant is the isolated environment that owns your mailboxes. Sendepot is the technology that automates everything inside the tenant; we don't bundle the tenant itself. You can buy one direct from Microsoft, source via a CSP partner, or order from us at $15/mo."
            />
            <Faq
              q="How many cold emails can I send per mailbox?"
              a="5 per day per mailbox is the safe ceiling. With 100 mailboxes per tenant (the default), that's 500/day per domain. 10 tenants = 5,000 cold sends per day. You can size each tenant 25–150 mailboxes when you create it. Going past 5/mailbox is how tenants get killed by Microsoft."
            />
            <Faq
              q="Do these inboxes deliver as well as Outlook accounts I'd buy from a reseller?"
              a="Yes — they ARE the same Azure inboxes. Resellers don't have access to a different M365 product than you do; they just charge a markup for clicking through provisioning. Use the same warm-up + copy hygiene either way."
            />
            <Faq
              q="Which sequencers work?"
              a="Instantly and Smartlead are live — Sendepot uploads every mailbox automatically the moment it's provisioned. More sequencers coming soon. For sequencers we don't support yet, export the credentials CSV and use their native uploader."
            />
            <Faq
              q="How do I drive Sendepot from Claude or another agent?"
              a={
                <>
                  Mint an API token, add Sendepot as an MCP server in your client.{' '}
                  <Link to="/docs/agents/overview" className="text-accent hover:underline">Step-by-step in the docs →</Link>
                </>
              }
            />
            <Faq
              q="Can I cancel and keep my mailboxes?"
              a="Tenants you provision through Sendepot are accessible only via Sendepot — we rotate admin passwords during setup so the original CSP can't reach them. Cancellation pauses new provisioning; existing tenants keep running. Re-subscribing later requires 3 months paid in advance at current rates."
            />
            <Faq
              q="What's the API like?"
              a={
                <>
                  REST + MCP, both backed by the same OpenAPI spec.{' '}
                  <a href="/api/docs" className="text-accent hover:underline">Browse the reference →</a>{' '}
                  Token auth, scoped to your workspace, can't see anyone else's data.
                </>
              }
            />
          </div>
        </section>

        {/* ── Final CTA ─────────────────────────────────────────────── */}
        <section className="max-w-4xl mx-auto px-8 pb-24">
          <div className="card p-12 text-center relative overflow-hidden">
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[500px] h-[300px] rounded-full bg-accent/10 blur-3xl" />
            </div>
            <div className="relative">
              <h2 className="font-serif text-3xl md:text-5xl tracking-tight mb-4">
                Cold-email infra, <span className="italic">automated.</span>
              </h2>
              <p className="text-sm md:text-base text-dim leading-relaxed max-w-xl mx-auto mb-8">
                Stop overpaying resellers. Stop waiting weeks for inboxes
                to land. Pick a plan and your first tenant ships same-day.
              </p>
              <div className="flex items-center justify-center gap-3 flex-wrap">
                <Link to="/signup" className="btn-primary btn-lg">Get started →</Link>
                <Link to="/docs/getting-started/quick-start" className="btn btn-lg">Read the docs</Link>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="max-w-6xl mx-auto px-8 py-10 border-t border-line/60 flex items-center justify-between text-xs text-dim flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <Wordmark className="h-4 opacity-70" />
            <span className="opacity-50">— the engine behind your outbound sending</span>
          </div>
          <div className="flex items-center gap-5">
            <Link to="/docs" className="hover:text-ink transition">Docs</Link>
            <a href="/api/docs" target="_blank" rel="noopener noreferrer" className="hover:text-ink transition">API & MCP ↗</a>
            <Link to="/login" className="hover:text-ink transition">Sign in</Link>
            <a href="mailto:support@sendepot.com" className="hover:text-ink transition">Support</a>
            <Link to="/terms" className="hover:text-ink transition">Terms</Link>
            <Link to="/privacy" className="hover:text-ink transition">Privacy</Link>
          </div>
        </footer>
      </div>
    </div>
  );
}


// ── Components ────────────────────────────────────────────────────────────

function Stat({ n, label }: { n: string; label: string }) {
  return (
    <div className="card p-5 text-center">
      <div className="font-serif text-3xl md:text-4xl tracking-tight text-ink mb-1">{n}</div>
      <div className="text-xs text-dim uppercase tracking-wider">{label}</div>
    </div>
  );
}

function FeatureCard({ kicker, title, body }: { kicker: string; title: string; body: string }) {
  return (
    <div className="card p-6 hover:bg-white/[0.04] transition">
      <div className="text-[10px] font-mono text-accent uppercase tracking-[0.18em] mb-3">{kicker}</div>
      <div className="font-serif text-lg tracking-tight text-ink mb-2">{title}</div>
      <div className="text-sm text-dim leading-relaxed">{body}</div>
    </div>
  );
}

function Step({ n, title, body }: { n: string; title: string; body: string }) {
  return (
    <div className="card p-5">
      <div className="flex items-baseline gap-3 mb-2">
        <span className="font-serif text-2xl text-accent leading-none">{n}</span>
        <span className="font-semibold text-ink">{title}</span>
      </div>
      <div className="text-sm text-dim leading-relaxed pl-8">{body}</div>
    </div>
  );
}

function PriceCard({
  tier, price, cadence, tagline, features, popular,
  ctaLabel = 'Get started', ctaHref,
}: {
  tier: string; price: string; cadence: string; tagline: string;
  features: string[]; popular?: boolean;
  ctaLabel?: string;
  // External href (e.g. mailto for the Enterprise tier). Omit to fall
  // back to the in-app /signup route.
  ctaHref?: string;
}) {
  const btnClass = popular ? 'btn-primary btn-lg w-full text-center' : 'btn btn-lg w-full text-center';
  return (
    <div className={`card p-6 relative flex flex-col ${popular ? 'shadow-[inset_0_0_0_1px_rgba(139,92,246,0.4)]' : ''}`}>
      {popular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-2.5 py-0.5 rounded-full bg-accent text-white text-[10px] uppercase tracking-wider font-semibold">
          Most popular
        </div>
      )}
      <div className="text-[11px] uppercase tracking-[0.18em] text-dim mb-2">{tier}</div>
      <div className="flex items-baseline gap-1 mb-1">
        <span className="font-serif text-4xl tracking-tight text-ink">{price}</span>
        {cadence && <span className="text-sm text-dim">{cadence}</span>}
      </div>
      <p className="text-xs text-dim mb-5">{tagline}</p>
      <ul className="space-y-2 mb-6 flex-1">
        {features.map(f => (
          <li key={f} className="flex items-start gap-2 text-sm text-ink">
            <span className="text-accent mt-0.5">✓</span>
            <span>{f}</span>
          </li>
        ))}
      </ul>
      {ctaHref ? (
        <a href={ctaHref} className={btnClass}>{ctaLabel}</a>
      ) : (
        <Link to="/signup" className={btnClass}>{ctaLabel}</Link>
      )}
    </div>
  );
}

function Faq({ q, a }: { q: string; a: React.ReactNode }) {
  return (
    <details className="card p-5 group">
      <summary className="cursor-pointer list-none flex items-center justify-between gap-4">
        <span className="text-sm font-semibold text-ink">{q}</span>
        <span className="text-dim group-open:rotate-45 transition-transform text-lg leading-none">+</span>
      </summary>
      <div className="text-sm text-dim leading-relaxed mt-3 pt-3 border-t border-line/40">
        {a}
      </div>
    </details>
  );
}
